import { styled } from "@mui/material/styles";
import { useField, useFormikContext } from "formik";
import {
  FC,
  Fragment,
  ReactElement,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useGetListQuery } from "../../features/api/Api";
import { IOption } from "../../interfaces/Utils";
import { isExists, locale, params } from "../../utils";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
} from "@mui/material";
import AdornmentLinkButton from "../buttons/AdormentLinkButton";
import { useSearchDebounce } from "../../hooks";

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#0063cc",
  },
  "& label.Mui-disabled": {
    color:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#0063cc",
  },
  ".MuiOutlinedInput-input.Mui-disabled": {
    textFillColor:
      localStorage.getItem("dark") === "true"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e5e5e5",
    },
    "&.Mui-disabled": {
      color:
        localStorage.getItem("dark") === "true"
          ? "rgba(255, 255, 255, 0.38)"
          : "rgba(0, 0, 0, 0.38)",
      fieldset: {
        borderColor:
          localStorage.getItem("dark") === "true"
            ? "rgba(255, 255, 255, 0.38)"
            : "rgba(0, 0, 0, 0.38)",
      },
    },
    "&:hover fieldset": {
      borderColor: "#0063cc",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#0063cc",
    },
  },
});

interface IReferenceSelect {
  name: string;
  label: string;
  reference: string;
  query?: any;
  optionName: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: { value: string | number; label: string };
  optionId?: string;
  skip?: boolean;
  secondOptionName?: string;
  optionNamePrefix?: string;
  localePrefix?: string;
  adornment?: ReactElement | undefined;
  redirectEditPageUrl?: string;
  readOnly?: boolean;
  secondField?:
    | {
        name: any;
        value: any;
      }
    | undefined;
  isSorting?: boolean;
  filteredIds?: string[];
  idRef?: any;
  labelRef?: any;
  secondLabelRef?: any;
  onChange?: any;
}

interface IReferenceSelect {
  name: string;
  label: string;
  reference: string;
  query?: any;
  size?: number;
  optionName: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: { value: string | number; label: string };
  optionId?: string;
  skip?: boolean;
  secondOptionName?: string;
  optionNamePrefix?: string;
  localePrefix?: string;
  adornment?: ReactElement | undefined;
  redirectEditPageUrl?: string;
  readOnly?: boolean;
  secondField?:
    | {
        name: any;
        value: any;
      }
    | undefined;
  isSorting?: boolean;
  filteredIds?: string[];
}

export function setFilter(
  query: any,
  search: string,
  id: string | number | null = null
) {
  let customQuery = {};
  if (query) {
    customQuery = { ...query };
  }

  if (search) {
    customQuery = { ...customQuery, name: search };
  }

  if (id) {
    customQuery = { ...customQuery, id };
  }

  if (Object.keys(customQuery).length > 0) {
    return customQuery;
  } else {
    return {};
  }
}

const ReferenceDropDown: FC<IReferenceSelect> = (props) => {
  const {
    name,
    label,
    reference,
    query,
    optionName,
    secondOptionName,
    optionNamePrefix,
    optionId,
    placeholder,
    disabled,
    skip,
    localePrefix,
    secondField,
    defaultValue,
    adornment,
    redirectEditPageUrl,
    readOnly,
    isSorting = true,
    filteredIds,
    size = 1000,
  } = props;
  //const { mode } = useTheme();
  const { setFieldValue }: any = useFormikContext();
  const [field, meta] = useField(name);
  const [options, setOptions] = useState<any[]>([]);
  const [search, setSearchQuery] = useSearchDebounce();
  const [fetchParams, setFetchParams] = useState({
    resource: reference,
    params: {
      ...params,
      pagination: { pageNumber: 0, size: size },
      filter: setFilter(query, search, field.value),
    },
  });

  const { data: currentData, isFetching } = useGetListQuery<any>(fetchParams, {
    skip: skip,
    refetchOnMountOrArgChange: true,
  });

  function searchQueryHandler(event: any) {
    setSearchQuery(event.target.value);
  }

  useEffect(() => {
    if (search) {
      setFetchParams((prev: any) => ({
        ...prev,
        resource: reference,
        params: {
          ...prev.params,
          filter: setFilter(query, search),
        },
      }));
    }

    if (query) {
      setFetchParams((prev: any) => ({
        ...prev,
        resource: reference,
        params: {
          ...prev.params,
          filter: setFilter(query, search),
        },
      }));
    }
  }, [search, setFetchParams, query, reference]);
  useEffect(() => {
    if (isExists(currentData?.data)) {
      let records: IOption[] = [];

      currentData.data.map((item: any) =>
        records.push({
          value: optionId ? item[optionId] : item.id,
          label: secondOptionName
            ? item[optionName] +
              (optionNamePrefix ? optionNamePrefix : " - ") +
              item[secondOptionName]
            : item[optionName],
        })
      );

      if (isSorting) {
        records = records.sort((a: IOption, b: IOption) =>
          a.label?.toLocaleLowerCase() > b.label?.toLocaleLowerCase() ? 1 : -1
        );
      }

      records = locale(records, localePrefix);

      const filteredRecords =
        filteredIds && filteredIds.length > 0
          ? records.filter(
              (record: IOption) =>
                !filteredIds.some((f: string) => f === record.value)
            )
          : records;

      setOptions(filteredRecords);
    }
  }, [
    currentData,
    defaultValue,
    localePrefix,
    optionId,
    optionName,
    secondOptionName,
    filteredIds,
    isSorting,
  ]);

  useEffect(() => {
    if (
      isExists(secondField?.name) &&
      isExists(currentData?.data) &&
      isExists(field?.value)
    ) {
      const { name, value }: any = secondField;
      let idx = optionId ? optionId : "id";
      const secondFieldValue = currentData.data.find(
        (item: any) => item[idx] === field.value
      )?.[value];

      if (isExists(secondField)) {
        setFieldValue(name, secondFieldValue, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, field.value]);

  function changeHandler(_event: any, newValue: any) {
    if (isExists(newValue)) {
      setFieldValue(field.name, newValue.value);
    } else {
      setFieldValue(field.name, "");
      setFetchParams((prev: any) => ({
        ...prev,
        params: { ...prev.params, filter: setFilter(query, "", "") },
      }));
    }
  }

  // const filterOptions = createFilterOptions({
  //   matchFrom: "start",
  //   stringify: (option: any) => option.label.toLocaleLowerCase("tr-TR"),
  // });

  return useMemo(() => {
    return (
      <Autocomplete
        isOptionEqualToValue={(_option, _value) => true}
        id={field.name}
        options={options}
        onChange={changeHandler}
        placeholder={placeholder}
        disabled={disabled}
        //filterOptions={filterOptions}
        loading={isFetching}
        readOnly={readOnly}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.value}>
            {option.label}
          </Box>
        )}
        value={
          isExists(field.value) && options.length > 0
            ? options.find((option) => option.value == field.value)
            : ""
        }
        sx={{ width: "100%" }}
        renderInput={(params) => (
          <CssTextField
            {...params}
            label={label}
            error={Boolean(meta.error)}
            helperText={meta.error}
            onChange={searchQueryHandler}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {isFetching && <CircularProgress color="primary" size={20} />}
                  {Boolean(adornment) && isExists(field.value) && adornment}
                  {params.InputProps.endAdornment}
                  {isExists(redirectEditPageUrl) && (
                    <AdornmentLinkButton path={redirectEditPageUrl ?? ""} />
                  )}
                </Fragment>
              ),
            }}
          />
        )}
      />
    );
  }, [
    field.value,
    meta.error,
    isFetching,
    adornment,
    options,
    currentData?.data,
  ]);
};

export default ReferenceDropDown;
